import React, { useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  IconButton,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { inputHelper } from "../../Helper";
import {
  useCreateQuizMutation,
  useGetQuizByIdQuery,
  useUpdateQuizMutation,
} from "../../Apis/quizApi";
import { useGetCampaignsQuery } from "../../Apis/campaignApi";
import { withAdminAuth } from "../../HOC";

interface ImageState {
  id: number;
  imageFile: File | null;
  imagePreviewUrl: string;
  captionTitle: string;
  captionDescription: string;
  order: number;
}

export default withAdminAuth(function QuizUpsert() {
  const { quizId } = useParams();
  const navigate = useNavigate();

  const [quizInputs, setQuizInputs] = useState({
    name: "",
    description: "",
    startDate: "",
    endDate: "",
    campaignId: "",
  });

  const [images, setImages] = useState<ImageState[]>([]);
  const [loading, setLoading] = useState(false);

  const [createQuiz] = useCreateQuizMutation();
  const [updateQuiz] = useUpdateQuizMutation();

  const { data: quizData } = useGetQuizByIdQuery(quizId!, { skip: !quizId });
  const { data: campaignsData, isLoading: isCampaignsLoading } =
    useGetCampaignsQuery(null);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({ open: false, message: "", severity: "info" });

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Load existing quiz data for editing
  useEffect(() => {
    if (quizId && quizData) {
      const { name, description, startDate, endDate, quizImages } =
        quizData.result;
      setQuizInputs({
        name,
        description,
        startDate: startDate.split("T")[0],
        endDate: endDate.split("T")[0],
        campaignId: quizData.result.campaignId,
      });

      setImages(
        quizImages.map((img: any, index: number) => ({
          id: index,
          imageFile: null,
          imagePreviewUrl: img.imageUrl,
          captionTitle: img.captionTitle,
          captionDescription: img.captionDescription,
          order: img.order,
        }))
      );
    }
  }, [quizData, quizId]);

  const handleQuizInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const tempData = inputHelper(e, quizInputs);
    setQuizInputs(tempData);
  };

  const handleImageChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      const imagePreviewUrl = URL.createObjectURL(file);
      const updatedImages = [...images];
      updatedImages[index] = {
        ...updatedImages[index],
        imageFile: file,
        imagePreviewUrl,
      };
      setImages(updatedImages);
    }
  };

  const handleImageCaptionChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number
  ) => {
    const { name, value } = e.target;
    const updatedImages = [...images];
    updatedImages[index] = { ...updatedImages[index], [name]: value };
    setImages(updatedImages);
  };

  const addImage = () => {
    const newImage: ImageState = {
      id: Date.now(),
      imageFile: null,
      imagePreviewUrl: "",
      captionTitle: "",
      captionDescription: "",
      order: images.length,
    };
    setImages([...images, newImage]);
  };

  const removeImage = (index: number) => {
    const updatedImages = images.filter((_, i) => i !== index);
    setImages(updatedImages.map((img, i) => ({ ...img, order: i })));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    if (!quizInputs.campaignId) {
      setSnackbar({
        open: true,
        message: "Please select a campaign.",
        severity: "error",
      });
      setLoading(false);
      return;
    }

    if (images.length === 0) {
      setSnackbar({
        open: true,
        message: "Please add at least one image.",
        severity: "error",
      });
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("Name", quizInputs.name);
    formData.append("Description", quizInputs.description);
    formData.append("StartDate", quizInputs.startDate);
    formData.append("EndDate", quizInputs.endDate);
    formData.append("CampaignId", quizInputs.campaignId.toString());

    images.forEach((image, index) => {
      if (image.imageFile)
        formData.append(`Images[${index}].image`, image.imageFile);
      if (image.imagePreviewUrl)
        formData.append(`Images[${index}].imageUrl`, image.imagePreviewUrl);
      formData.append(`Images[${index}].captionTitle`, image.captionTitle);
      formData.append(
        `Images[${index}].captionDescription`,
        image.captionDescription
      );
      formData.append(`Images[${index}].imageOrder`, image.order.toString());
    });

    try {
      if (quizId) {
        const response = await updateQuiz({ data: formData, id: quizId });
        if ("data" in response && response.data.isSuccess) {
          setSnackbar({
            open: true,
            message: "Quiz updated successfully!",
            severity: "success",
          });
          setTimeout(() => navigate("/Quiz/list"), 1000);
        } else {
          setSnackbar({
            open: true,
            message: "Failed to update quiz.",
            severity: "error",
          });
        }
      } else {
        const response = await createQuiz(formData);
        if ("data" in response && response.data.isSuccess) {
          setSnackbar({
            open: true,
            message: "Quiz Created successfully!",
            severity: "success",
          });
          setTimeout(() => navigate("/Quiz/list"), 1000);
        } else {
          setSnackbar({
            open: true,
            message: "Failed to create quiz.",
            severity: "error",
          });
        }
      }
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: "An error occurred during submission.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Card sx={{ boxShadow: 3, borderRadius: 3 }}>
        <CardHeader
          title={
            <Typography variant="h5" sx={{ color: "#eb3c96" }}>
              {!quizId ? "Create" : "Edit"} Quiz
            </Typography>
          }
        />
        <CardContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                {isCampaignsLoading ? (
                  <Typography>Loading campaigns...</Typography>
                ) : (
                  <TextField
                    select
                    fullWidth
                    label="Campaign"
                    name="campaignId"
                    value={quizInputs.campaignId}
                    onChange={handleQuizInput}
                    SelectProps={{ native: true }}
                    required
                    disabled={!!quizId}
                  >
                    <option value=""></option>
                    {campaignsData?.result
                      ?.filter((c: any) => c.isActive)
                      .map((campaign: any) => (
                        <option key={campaign.id} value={campaign.id}>
                          {campaign.name}
                        </option>
                      ))}
                  </TextField>
                )}
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Quiz Name"
                  name="name"
                  value={quizInputs.name}
                  onChange={handleQuizInput}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={quizInputs.description}
                  onChange={handleQuizInput}
                  multiline
                  rows={4}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  name="startDate"
                  value={quizInputs.startDate}
                  onChange={handleQuizInput}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  name="endDate"
                  value={quizInputs.endDate}
                  onChange={handleQuizInput}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
            </Grid>

            <Box mt={4}>
              <Typography variant="h6" mb={2}>
                Quiz Images
              </Typography>

              {images.map((image, index) => (
                <Card key={image.id} sx={{ mb: 2, p: 2, boxShadow: 1 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, md: 4 }}>
                      {image.imagePreviewUrl && (
                        <Box textAlign="center">
                          <img
                            src={image.imagePreviewUrl}
                            alt={`Preview ${index + 1}`}
                            style={{ maxWidth: "100%", height: "auto" }}
                          />
                        </Box>
                      )}
                      <Button
                        variant="outlined"
                        component="label"
                        sx={{ mt: 1 }}
                        fullWidth
                      >
                        Upload Image
                        <input
                          hidden
                          type="file"
                          onChange={(e) => handleImageChange(e, index)}
                        />
                      </Button>
                    </Grid>

                    <Grid size={{ xs: 12, md: 8 }}>
                      <TextField
                        fullWidth
                        label="Caption Title"
                        name="captionTitle"
                        value={image.captionTitle}
                        onChange={(e) => handleImageCaptionChange(e, index)}
                        required
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        fullWidth
                        label="Caption Description"
                        name="captionDescription"
                        value={image.captionDescription}
                        onChange={(e) => handleImageCaptionChange(e, index)}
                        multiline
                        rows={2}
                        required
                        sx={{ mb: 2 }}
                      />
                      <Box display="flex" justifyContent="flex-end">
                        <IconButton
                          color="error"
                          onClick={() => removeImage(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Grid>
                  </Grid>
                </Card>
              ))}

              <Box textAlign="right" mb={2}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addImage}
                >
                  Add Image
                </Button>
              </Box>
            </Box>

            <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
              <Button
                type="submit"
                variant="contained"
                color="success"
                disabled={loading}
                startIcon={loading && <CircularProgress size={18} />}
              >
                {quizId ? "Update" : "Create"}
              </Button>
              <Button
                variant="contained"
                sx={{ backgroundColor: "rgba(81, 35, 132, 1)" }}
                onClick={() => navigate("/quiz/list")}
              >
                Back to List
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
});
